import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { PaymentService } from '../../services/payments/payment/payment.service';
import { TransactionDetailResponse } from '../../models/payment/transactionDetailResponse.model';
import { PaymentConfirmationComponent } from '../../student-portal/psychometric-test/payment-confirmation/payment-confirmation.component';
import { PaymentConfirmation } from '../../models/psychometric-test/psychometric-test.model';
import { CardModule } from 'primeng/card';
import { MessageService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { UserStateModel } from '../../states/user/user-state-model';
import { AuthService } from '../../services/account/auth.service';
import { UserResponse } from '../../models/account/userResponse';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { ApprovalStatus } from '../../models/enums/approvalStatus';
import { LottieAnimationComponent } from '../../shared/components/lottie-animation/lottie-animation.component';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface PaymentData {
  orderId: string;
  transactionId: string;
  paymentDate: string;
  customer: {
    name: string;
    email: string;
    phone: string;
    address: string;
  };
  package: {
    name: string;
    services: string[];
  };
  amount: number;
}

@Component({
  selector: 'app-payment-success',
  standalone: true,
  imports: [CommonModule, ButtonModule, PaymentConfirmationComponent, CardModule, LottieAnimationComponent],
  providers: [MessageService],
  templateUrl: './payment-success.component.html',
  styleUrls: ['./payment-success.component.scss']
})

export class PaymentSuccessComponent implements OnInit {
  private paymentService = inject(PaymentService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private store = inject(Store);
  private authService = inject(AuthService);
  private superAdminService = inject(SuperAdminService);

  paymentData: PaymentData | null = null;
  transactionData: TransactionDetailResponse | null = null;
  loading = true;
  isStudentPortalPackage = false;
  paymentConfirmation: PaymentConfirmation | null = null;
  userDetails: UserResponse | null = null;

  ngOnInit(): void {
    const transactionId = this.route.snapshot.params['transactionId'];
    if (transactionId) {
      this.loadTransactionData(transactionId);
    } else {
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Transaction ID is missing'
      });
    }
  }

  private loadTransactionData(transactionId: string): void {
    this.paymentService.getTransaction(transactionId).subscribe({
      next: (data: TransactionDetailResponse) => {
        const isDirectApply = data.packageDetails?.systemAssignedPackageName?.toLowerCase()?.trim() === 'institute direct application' 
                           || data.packageDetails?.packageName?.toLowerCase()?.trim() === 'institute direct application'
                           || data.packageDetails?.systemAssignedPackageName?.toLowerCase()?.includes('direct application')
                           || data.packageDetails?.packageName?.toLowerCase()?.includes('direct application');

        if (isDirectApply) {
          this.router.navigate(['/portal/direct-apply'], { queryParams: { payment: 'review', success: 'True' }});
          return;
        }

        this.transactionData = data;
        this.mapToPaymentData(data);
        this.checkIfStudentPortalPackage(data);

        this.loadUserDetailsAndCreateConfirmation(data);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load transaction details. Please try again later.'
        });
        console.error('Error loading transaction:', error);
      }
    });
  }

  private mapToPaymentData(data: TransactionDetailResponse): void {
    this.paymentData = {
      orderId: data.transactionId,
      transactionId: data.transactionId,
      paymentDate: this.formatDate(data.paymentDate),
      customer: {
        name: data.customerDetails.name,
        email: data.customerDetails.email,
        phone: data.customerDetails.phone,
        address: data.customerDetails.address
      },
      package: {
        name: data.packageDetails.packageName,
        services: data.packageDetails.servicesIncluded
      },
      amount: data.paymentSummary.totalPaid
    };
  }

  private checkIfStudentPortalPackage(data: TransactionDetailResponse): void {
    this.isStudentPortalPackage =
      data.packageDetails.isReadOnly &&
      !!data.packageDetails.systemAssignedPackageName &&
      data.packageDetails.systemAssignedPackageName.toLowerCase() === 'psychometric test';

    if (this.isStudentPortalPackage) {
      this.store.selectOnce(UserSelectors.getUser).subscribe((user: UserStateModel) => {
        if (user?.id) {
          this.updateTestRequestStatus(user.id);
        }
      });
    }
  }

  private loadUserDetailsAndCreateConfirmation(data: TransactionDetailResponse): void {
    this.store.selectOnce(UserSelectors.getUser).subscribe((user: UserStateModel) => {
      if (user?.id) {
        this.authService.getUserByid(user.id).subscribe({
          next: (userDetails: UserResponse) => {
            this.userDetails = userDetails;
            this.createPaymentConfirmation(data, userDetails);
          },
          error: () => {
            this.createPaymentConfirmation(data, null);
          }
        });
      } else {
        this.createPaymentConfirmation(data, null);
      }
    });
  }

  private createPaymentConfirmation(data: TransactionDetailResponse, userDetails: UserResponse | null): void {
    const billingInfo = {
      fullName: data.customerDetails.name,
      emailAddress: data.customerDetails.email,
      billingAddress: data.customerDetails.address,
      phoneNumber: data.customerDetails.phone,
      agreeToTerms: true
    };

    this.paymentConfirmation = {
      orderId: data.transactionId,
      orderDate: this.formatDate(data.paymentDate),
      billingInfo: billingInfo,
      accountInfo: {
        accountHolder: userDetails ? `${userDetails.firstName} ${userDetails.lastName || ''}`.trim() : data.customerDetails.name,
        registeredEmail: userDetails?.email || data.customerDetails.email,
        registrationDate: this.formatDate(data.paymentDate)
      },
      orderSummary: {
        packageId: 0,
        testName: data.packageDetails.packageName,
        description: data.packageDetails.description || undefined,
        duration: '60 minutes',
        subtotal: data.paymentSummary.packageAmount,
        tax: data.paymentSummary.totalPaid - data.paymentSummary.packageAmount,
        total: data.paymentSummary.totalPaid,
        whatsIncluded: data.packageDetails.servicesIncluded
      },
      paymentMethod: 'Online Payment',
      paymentDate: this.formatDate(data.paymentDate)
    };
  }

  private updateTestRequestStatus(studentId: string): void {
    // Update test request status to Approved
    this.superAdminService.updateMindlerTestRequestStatus({
      studentId: studentId,
      status: ApprovalStatus.Approved
    }).subscribe({
      next: () => {
        // Status updated successfully
        // Backend should handle PaymentDone status automatically
      },
      error: (error) => {
        console.error('Error updating test request status:', error);
        // Don't show error to user as payment was successful
      }
    });
  }

  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateString;
    }
  }

  downloadReceipt(): void {
    const receiptPdfAreaElement = document.getElementById('receiptPdfArea');
    if (!receiptPdfAreaElement) {
        this.messageService.add({
            severity: 'error',
            summary: 'Receipt Downloading Error',
            detail: 'Failed to download receipt'
        });
        return;
    }

    (document as any).fonts.ready.then(() => {
        html2canvas(receiptPdfAreaElement, {
            scale: 3,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false,
            removeContainer: true
        }).then(canvas => {
            const imgData = canvas.toDataURL('image/png', 1.0);
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = 210;
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'NONE');
            pdf.save(`Gradpilots-Receipt-${this.paymentData?.orderId}.pdf`);

            this.messageService.add({
                severity: 'success',
                summary: 'Receipt Downloaded',
                detail: 'Your receipt has been downloaded successfully'
            });
        });
    });
  }

  makeAnotherPayment(): void {
    this.router.navigate(['/pay-now']);
  }

  goToTestLaunch(): void {
    this.router.navigate(['/portal/psychometric-test'], { queryParams: { justPaid: 'true' } });
  }
}

